import { useMemo } from "react";
import { useData } from "@/context/DataContext";
import AmmTabs from "@/components/AmmTabs";
import { CalendarClock } from "lucide-react";
import { formatDate } from "@/lib/tp";

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function AmmScadenze() {
  const { clients } = useData();

  const scadenzeByMonth = useMemo(() => {
    const map = new Map();
    for (const c of clients) {
      if (!c.contract_expiry) continue;
      const d = new Date(c.contract_expiry);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ client: c, date: d });
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, items]) => {
        const [y, m] = k.split("-").map(Number);
        return { key: k, year: y, month: m, label: `${MONTH_NAMES[m - 1]} ${y}`, items: items.sort((a, b) => a.date - b.date) };
      });
  }, [clients]);

  const total = scadenzeByMonth.reduce((s, m) => s + m.items.length, 0);

  return (
    <div className="space-y-6 tp-fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interna</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Amministrazione</h1>
      </div>

      <AmmTabs />

      <section className="tp-glass rounded-3xl overflow-hidden" data-testid="scadenze-calendario">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <CalendarClock size={14} className="text-fuchsia-400" />
          <h2 className="text-base font-semibold text-white">Scadenze clienti</h2>
          <span className="ml-auto text-xs text-zinc-500 tabular-nums">{total} totali</span>
        </div>
        <div className="p-4 space-y-4">
          {scadenzeByMonth.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500 italic">
              Nessuna scadenza. Imposta una data di scadenza contratto nella scheda del cliente.
            </div>
          ) : (
            scadenzeByMonth.map((mo) => (
              <div
                key={mo.key}
                data-testid={`scadenza-month-${mo.key}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
              >
                <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-fuchsia-400/80">{mo.label}</span>
                  <span className="ml-auto text-[11px] text-zinc-500 tabular-nums">{mo.items.length}</span>
                </div>
                <ul className="divide-y divide-white/5">
                  {mo.items.map(({ client }) => (
                    <li key={client.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: client.color }} />
                      <span className="text-sm text-white flex-1 truncate">{client.name}</span>
                      <span className="text-xs text-zinc-400 tabular-nums">{formatDate(client.contract_expiry)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
